import { useState, useMemo } from 'react'
import { useStampCards } from './hooks/useStampCards'
import { useMembers } from '../../hooks/useMembers'
import { useAuth } from '../../hooks/useAuth'
import { Spinner } from '../../components/ui/Spinner'
import { Button } from '../../components/ui/Button'
import { cn } from '../../lib/utils'
import { Portal } from '../../components/Portal'

// ─── Create card form ─────────────────────────────────────────────────────────
const CreateCardModal = ({ members, onCreate, onClose }) => {
  const [name,           setName]           = useState('')
  const [goalCount,      setGoalCount]      = useState('10')
  const [reward,         setReward]         = useState('')
  const [assignedTo,     setAssignedTo]     = useState(members[0]?.profiles?.id ?? '')
  const [linkedTask,     setLinkedTask]     = useState('')
  const [saving,         setSaving]         = useState(false)

  const submit = async (e) => {
    e.preventDefault()
    if (!name.trim() || !assignedTo) return
    setSaving(true)
    try {
      await onCreate({
        name:               name.trim(),
        goal_count:         parseInt(goalCount),
        reward_description: reward || null,
        assigned_to:        assignedTo,
        linked_task_title:  linkedTask.trim() || null,
      })
      onClose()
    } finally { setSaving(false) }
  }

  const limitedMembers = members.filter((m) => m.role === 'limited' && m.profiles)

  return (
    <Portal>
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <form
        className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90dvh] flex flex-col shadow-card"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border flex-shrink-0">
          <h2 className="text-base font-bold text-text-primary">New Stamp Card</h2>
          <button type="button" onClick={onClose} className="text-text-muted p-1">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4 space-y-4">
          <input type="text" placeholder="Card name (e.g. Bedroom Chores)" value={name} onChange={(e) => setName(e.target.value)} autoFocus
            className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-text-muted mb-1 block">Assign to</label>
              <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}
                className="w-full rounded-base border border-border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary">
                {(limitedMembers.length ? limitedMembers : members.filter((m) => m.profiles)).map((m) => (
                  <option key={m.profiles.id} value={m.profiles.id}>{m.profiles.display_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-text-muted mb-1 block">Stamps to fill</label>
              <input type="number" min="1" max="50" value={goalCount} onChange={(e) => setGoalCount(e.target.value)}
                className="w-full rounded-base border border-border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
          </div>

          <input type="text" placeholder="Reward description" value={reward} onChange={(e) => setReward(e.target.value)}
            className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />

          <div>
            <label className="text-xs text-text-muted mb-1 block">Auto-award when task completed (optional)</label>
            <input type="text" placeholder="Task title to link…" value={linkedTask} onChange={(e) => setLinkedTask(e.target.value)}
              className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="secondary" size="sm" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button type="submit" size="sm" className="flex-1" disabled={saving}>{saving ? <Spinner size="sm" /> : 'Create Card'}</Button>
          </div>
        </div>
      </form>
    </div>
    </Portal>
  )
}

// ─── Stamp grid (visual) ──────────────────────────────────────────────────────
const StampGrid = ({ count, goal, color }) => {
  const stamps = Array.from({ length: goal }, (_, i) => i < count)
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {stamps.map((filled, i) => (
        <div
          key={i}
          className={cn('w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all',
            filled ? 'border-transparent scale-105' : 'border-border bg-background')}
          style={filled ? { backgroundColor: color ?? '#2d1b5e' } : {}}
        >
          {filled && (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          )}
        </div>
      ))}
    </div>
  )
}

// ─── Admin card ───────────────────────────────────────────────────────────────
const AdminCard = ({ card, memberMap, onAward, onRedeem, onDelete }) => {
  const member  = memberMap[card.assigned_to]
  const full    = card.current_count >= card.goal_count
  const color   = member?.member_color

  return (
    <div className={cn('bg-surface rounded-xl border p-4', full && !card.completed_at ? 'border-amber-300 bg-amber-50' : 'border-border')}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <div>
          <h3 className="font-semibold text-sm text-text-primary">{card.name}</h3>
          {member && (
            <span className="text-[11px] font-medium px-2 py-0.5 rounded-full text-white mt-0.5 inline-block"
              style={{ backgroundColor: color ?? '#7c6faf' }}>
              {member.display_name}
            </span>
          )}
        </div>
        <button onClick={() => onDelete(card.id)} className="text-text-muted hover:text-rose-500 flex-shrink-0">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
        </button>
      </div>

      <div className="text-xs text-text-muted mb-2">
        {card.current_count}/{card.goal_count} stamps
        {card.reward_description && <span className="ml-2">· Reward: {card.reward_description}</span>}
      </div>

      <StampGrid count={card.current_count} goal={card.goal_count} color={color} />

      {full && !card.completed_at && (
        <div className="mt-3 flex items-center gap-2 p-2 bg-amber-100 rounded-xl">
          <span className="text-xs font-semibold text-amber-700 flex-1">🎉 Card complete! Ready to redeem.</span>
          <Button size="sm" onClick={() => onRedeem(card.id)}
            className="bg-amber-500 hover:bg-amber-600 text-white border-amber-500 flex-shrink-0">
            Mark Redeemed
          </Button>
        </div>
      )}

      {!full && (
        <Button size="sm" variant="secondary" onClick={() => onAward(card)} className="mt-3">
          + Award Stamp
        </Button>
      )}

      {card.linked_task_title && (
        <div className="text-[10px] text-text-muted mt-2">Auto-awards when: "{card.linked_task_title}"</div>
      )}
    </div>
  )
}

// ─── Member card ──────────────────────────────────────────────────────────────
const MemberCard = ({ card, color }) => (
  <div className={cn('bg-surface rounded-xl border p-4', card.current_count >= card.goal_count ? 'border-amber-300' : 'border-border')}>
    <h3 className="font-semibold text-sm text-text-primary">{card.name}</h3>
    <div className="text-xs text-text-muted mb-2">{card.current_count}/{card.goal_count} stamps</div>
    <StampGrid count={card.current_count} goal={card.goal_count} color={color} />
    {card.reward_description && (
      <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1 mt-3">
        Reward: {card.reward_description}
      </div>
    )}
    {card.current_count >= card.goal_count && (
      <div className="text-xs font-bold text-amber-600 mt-2">🎉 Complete! Ask your admin to redeem.</div>
    )}
  </div>
)

// ─── Main page ────────────────────────────────────────────────────────────────
export const RewardsPage = () => {
  const { user, isAdmin }           = useAuth()
  const { data: members = [] }      = useMembers()
  const { data: cards = [], isLoading, createCard, awardStamp, markRedeemed, deleteCard } = useStampCards()
  const [showCreate, setShowCreate] = useState(false)

  const memberMap = useMemo(() => {
    const map = {}
    for (const m of members) {
      if (m.profiles) map[m.profiles.id] = m.profiles
    }
    return map
  }, [members])

  const myCards = cards.filter((c) => c.assigned_to === user?.id)

  if (isLoading) return <div className="flex justify-center py-16"><Spinner /></div>

  if (isAdmin) {
    return (
      <div className="px-4 py-5 max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-5">
          <h1 className="text-xl font-bold text-text-primary">Rewards</h1>
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 bg-primary text-white text-sm font-medium px-4 py-2 rounded-xl hover:bg-primary/90">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
            New Card
          </button>
        </div>

        {cards.length === 0 ? (
          <div className="text-center py-12 text-text-muted">
            <p className="text-sm">No stamp cards yet. Create one for a household member!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {cards.map((card) => (
              <AdminCard key={card.id} card={card} memberMap={memberMap}
                onAward={awardStamp} onRedeem={markRedeemed} onDelete={deleteCard} />
            ))}
          </div>
        )}

        {showCreate && (
          <CreateCardModal members={members} onCreate={createCard} onClose={() => setShowCreate(false)} />
        )}
      </div>
    )
  }

  // Limited member view
  const myColor = memberMap[user?.id]?.member_color
  return (
    <div className="px-4 py-5 max-w-2xl mx-auto">
      <h1 className="text-xl font-bold text-text-primary mb-5">My Rewards</h1>
      {myCards.length === 0 ? (
        <p className="text-sm text-text-muted text-center py-12">No stamp cards assigned to you yet</p>
      ) : (
        <div className="space-y-3">
          {myCards.map((card) => <MemberCard key={card.id} card={card} color={myColor} />)}
        </div>
      )}
    </div>
  )
}
